/**
 * Update File Mutation Hooks
 *
 * React Query mutation hooks for file update operations
 */

'use client';

import {
  useMutation,
  useQueryClient,
  type UseMutationResult
} from '@tanstack/react-query';
import { toast } from '@/lib/toast';
import { filesApi } from '../client';
import type {
  CoreSlotFileType,
  IdentitySlotFileType,
  MatterFile,
  UpdateFileRequest,
  FileListResponse
} from '../types';
import { createFileErrorHandler, shouldRetry } from './helpers';
import { fileKeys, taskKeys, timelineKeys } from '@/lib/query-keys';
import { getDependencies } from '@/lib/services/query-dependencies';
import { CORE_SLOT_LABELS } from '../file-types';

// ============================================================================
// UPDATE FILE
// ============================================================================

/**
 * Hook to update a file's metadata
 *
 * @returns Mutation object for updating files
 *
 * @example
 * ```tsx
 * const updateMutation = useUpdateFile();
 * updateMutation.mutate({
 *   matterId: 1,
 *   fileId: 123,
 *   data: { displayName: 'New Name' }
 * });
 * ```
 */
export function useUpdateFile(): UseMutationResult<
  MatterFile,
  unknown,
  { matterId: number; fileId: number; data: UpdateFileRequest },
  {
    snapshots: Array<
      readonly [readonly unknown[], FileListResponse | undefined]
    >;
  }
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ matterId, fileId, data }) => {
      return filesApi.updateFile(matterId, fileId, data);
    },

    // Retry configuration
    retry: shouldRetry,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),

    /*
      **Land the change in the list the documents drawer renders from before the
      request leaves** (`components/attributes/CLAUDE.md` §6, VALE-709). The
      drawer's attribute rows (`Note`, `Certified copy`) read the file out of
      `fileKeys.forMatter`'s list, close their editor the instant the commit is
      called, and this hook only *invalidated* that list, so a note repainted
      its previous text for the whole round trip plus the refetch. Every list
      variant is patched (the tab reads filtered lists too), and a refusal
      restores the snapshots so the re-opened field sits over what the server
      holds (VALE-1505).
    */
    onMutate: async ({ matterId, fileId, data }) => {
      await queryClient.cancelQueries({
        queryKey: fileKeys.forMatter(matterId)
      });
      const snapshots = queryClient.getQueriesData<FileListResponse>({
        queryKey: fileKeys.forMatter(matterId)
      });
      queryClient.setQueriesData<FileListResponse>(
        { queryKey: fileKeys.forMatter(matterId) },
        (old) =>
          old
            ? {
                ...old,
                files: old.files.map((file) =>
                  file.id === fileId ? { ...file, ...data } : file
                )
              }
            : old
      );
      return { snapshots };
    },

    onSuccess: (updatedFile, variables) => {
      // Update the file in cache
      queryClient.setQueryData(fileKeys.item(variables.fileId), updatedFile);

      // Invalidate files list to refetch
      queryClient.invalidateQueries({
        queryKey: fileKeys.forMatter(variables.matterId)
      });

      // Invalidate tasks and history as they depend on document status
      queryClient.invalidateQueries({
        queryKey: taskKeys.dashboard(variables.matterId, 0)
      });
      queryClient.invalidateQueries({
        queryKey: taskKeys.dashboard(variables.matterId, 1)
      });
      queryClient.invalidateQueries({
        queryKey: timelineKeys.forMatter(variables.matterId)
      });
      queryClient.invalidateQueries({
        queryKey: timelineKeys.history(variables.matterId)
      });

      toast.success('Document updated successfully');
    },

    onError: (error, _variables, context) => {
      context?.snapshots.forEach(([key, snapshot]) => {
        queryClient.setQueryData(key, snapshot);
      });
      createFileErrorHandler('update')(error);
    }
  });
}

// ============================================================================
// UNCATEGORISE FILE (LF-5263)
// ============================================================================

/**
 * Hook to uncategorise a file from a core document slot, moving it back into
 * Additional Documents.
 *
 * @returns Mutation object for uncategorising files
 *
 * @example
 * ```tsx
 * const uncategoriseMutation = useUncategoriseFile();
 * uncategoriseMutation.mutate({ matterId: 1, fileId: 123 });
 * ```
 */
export function useUncategoriseFile(): UseMutationResult<
  MatterFile,
  unknown,
  { matterId: number; fileId: number }
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ matterId, fileId }) => {
      return filesApi.uncategoriseFile(matterId, fileId);
    },

    // Retry configuration
    retry: shouldRetry,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),

    onSuccess: (updatedFile, variables) => {
      // Update the file in cache
      queryClient.setQueryData(fileKeys.item(variables.fileId), updatedFile);

      // Invalidate files list so the card moves from its core slot to Additional
      // Documents on refetch (placement is driven by fileType).
      queryClient.invalidateQueries({
        queryKey: fileKeys.forMatter(variables.matterId)
      });

      // Invalidate tasks and history as they depend on document status
      queryClient.invalidateQueries({
        queryKey: taskKeys.dashboard(variables.matterId, 0)
      });
      queryClient.invalidateQueries({
        queryKey: taskKeys.dashboard(variables.matterId, 1)
      });
      queryClient.invalidateQueries({
        queryKey: timelineKeys.forMatter(variables.matterId)
      });
      queryClient.invalidateQueries({
        queryKey: timelineKeys.history(variables.matterId)
      });

      toast.success('Document moved to Additional Documents');
    },

    onError: createFileErrorHandler('update')
  });
}

// ============================================================================
// CATEGORISE FILE (VALE-806)
// ============================================================================

/**
 * Hook to mark a file as one of the matter's core documents — the inverse of
 * `useUncategoriseFile`, and the way a lawyer corrects an AI misclassification.
 *
 * Displacement happens server-side in one transaction: whatever already carries
 * the target type is moved back into Additional Documents, so the two documents
 * swap places in a single refetch.
 *
 * @example
 * ```tsx
 * const categoriseMutation = useCategoriseFile();
 * categoriseMutation.mutate({ matterId: 1, fileId: 123, fileType: 'will' });
 * ```
 */
export function useCategoriseFile(): UseMutationResult<
  MatterFile,
  unknown,
  { matterId: number; fileId: number; fileType: CoreSlotFileType }
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ matterId, fileId, fileType }) => {
      return filesApi.categoriseFile(matterId, fileId, fileType);
    },

    retry: shouldRetry,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),

    onSuccess: (updatedFile, variables) => {
      queryClient.setQueryData(fileKeys.item(variables.fileId), updatedFile);

      // The single-source dependency map, not a hand-listed set: this mutation
      // moves a document between buckets AND can change a draft correspondence's
      // attachments, and `document` already owns both surfaces.
      for (const queryKey of getDependencies(
        'document',
        variables.matterId,
        variables.fileId
      )) {
        queryClient.invalidateQueries({ queryKey });
      }

      toast.success(
        `Document marked as ${CORE_SLOT_LABELS[variables.fileType]}`
      );
    },

    onError: createFileErrorHandler('update')
  });
}

// ============================================================================
// ASSIGN IDENTITY DOCUMENT (VALE-806)
// ============================================================================

/**
 * Hook to move an identity document onto a different contact.
 *
 * Same displacement policy as `useCategoriseFile` on a different owner entity:
 * the contact's incumbent identity document is moved back into Additional
 * Documents, and the file is released from whichever contact previously held it.
 */
export function useAssignIdentityDocument(): UseMutationResult<
  MatterFile,
  unknown,
  {
    matterId: number;
    fileId: number;
    contactId: number;
    fileType: IdentitySlotFileType;
    contactName?: string;
  }
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ matterId, fileId, contactId, fileType }) => {
      return filesApi.assignIdentityDocument(
        matterId,
        fileId,
        contactId,
        fileType
      );
    },

    retry: shouldRetry,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),

    onSuccess: (updatedFile, variables) => {
      queryClient.setQueryData(fileKeys.item(variables.fileId), updatedFile);

      // Both entities move: the document changes bucket and the contact's
      // identification pointer changes, and the contact dialog reads the latter.
      const dependencies = [
        ...getDependencies('document', variables.matterId, variables.fileId),
        ...getDependencies('contact', variables.matterId, variables.contactId)
      ];
      for (const queryKey of dependencies) {
        queryClient.invalidateQueries({ queryKey });
      }

      toast.success(
        variables.contactName
          ? `Identity document moved to ${variables.contactName}`
          : 'Identity document moved'
      );
    },

    onError: createFileErrorHandler('update')
  });
}
